import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * MasteryMark (spec §7.3, §9.3) — the standing on ONE mission.
 *
 * Two hard rules the component encodes rather than documents:
 *
 *  - **Never colour alone** (spec §12). Each level carries its own glyph
 *    *shape* (empty ring → quarter → three quarters → filled with a check) and
 *    a written label. Turning the stylesheet off, printing in greyscale or
 *    viewing with a colour-vision deficiency all still yield four
 *    distinguishable states.
 *  - **Always say why.** The level is a competence claim, so the reasons that
 *    hold it there travel with it — visibly when `showWhy`, otherwise in the
 *    accessibility tree. Reason codes come from `classifyLevel()` and are
 *    mapped to i18n here; a raw code is never rendered.
 */

const LEVELS = ['new', 'seen', 'practiced', 'secure'];

/** Distinct silhouette per level — the non-colour carrier of the information. */
function LevelGlyph({ level }) {
  switch (level) {
    case 'secure':
      return (
        <svg viewBox="0 0 16 16" width="100%" height="100%" focusable="false" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path
            d="M4.6 8.3 6.9 10.6 11.4 5.6"
            fill="none"
            stroke="var(--color-surface-raised)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'practiced':
      return (
        <svg viewBox="0 0 16 16" width="100%" height="100%" focusable="false" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M8 8 8 1 A7 7 0 1 1 1 8 Z" fill="currentColor" />
        </svg>
      );
    case 'seen':
      return (
        <svg viewBox="0 0 16 16" width="100%" height="100%" focusable="false" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M8 8 8 1 A7 7 0 0 1 15 8 Z" fill="currentColor" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 16 16" width="100%" height="100%" focusable="false" aria-hidden="true">
          <circle
            cx="8"
            cy="8"
            r="7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="3 3"
          />
        </svg>
      );
  }
}

/**
 * Human sentence explaining why a level holds, built from the level hint plus
 * the outstanding requirements.
 *
 * @param {Function} t react-i18next translate
 * @param {string} level
 * @param {string[]} reasons reason codes from `classifyLevel()`
 */
export function masteryWhy(t, level, reasons = []) {
  const safeLevel = LEVELS.includes(level) ? level : 'new';
  const hint = t(`mastery.level.${safeLevel}Hint`);
  const codes = (Array.isArray(reasons) ? reasons : []).filter(
    (code) => typeof code === 'string' && code !== 'secure'
  );
  if (codes.length === 0) return hint;
  const detail = codes.map((code) => t([`mastery.reason.${code}`, 'mastery.notYetSecure'])).join(' · ');
  return `${hint} ${detail}`;
}

/**
 * @param {object} props
 * @param {'new'|'seen'|'practiced'|'secure'} [props.level='new']
 * @param {string[]} [props.reasons] reason codes from the mastery model.
 * @param {'compact'|'default'} [props.size='default']
 * @param {boolean} [props.showLabel=true] hide only where a visible label sits
 *   immediately next to the mark; the accessible text is kept either way.
 * @param {boolean} [props.showWhy=false] render the explanation visibly.
 * @param {string} [props.className]
 */
export const MasteryMark = forwardRef(function MasteryMark(
  { level = 'new', reasons = [], size = 'default', showLabel = true, showWhy = false, className = '', ...rest },
  ref
) {
  const { t } = useTranslation();
  const safeLevel = LEVELS.includes(level) ? level : 'new';
  const label = t(`mastery.level.${safeLevel}`);
  const why = masteryWhy(t, safeLevel, reasons);

  return (
    <span
      ref={ref}
      className={`etch-mastery-mark ${className}`.trim()}
      data-level={safeLevel}
      data-size={size === 'default' ? undefined : size}
      {...rest}
    >
      <span className="etch-mastery-mark__glyph" aria-hidden="true">
        <LevelGlyph level={safeLevel} />
      </span>
      <span className={showLabel ? 'etch-mastery-mark__label' : 'etch-visually-hidden'}>{label}</span>
      {showWhy ? (
        <span className="etch-mastery-mark__why">{why}</span>
      ) : (
        <span className="etch-visually-hidden">{why}</span>
      )}
    </span>
  );
});

export default MasteryMark;
